import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Footer } from '../landing/Footer.js';
import { Logo } from '../landing/Logo.js';

/**
 * The shell for the public documents.
 *
 * It borrows the landing page's composition — paper ground, one plate, a
 * hairline instead of a shadow — and drops its backdrop video: these are pages
 * someone reads carefully, prints, or sends to their lawyer, and moving footage
 * behind a retention table is noise. The header is a way back to the site and
 * nothing else; there is no dashboard chrome on a page a logged-out visitor is
 * meant to reach.
 */

export interface LegalLayoutProps {
  label: string;
  title: string;
  standfirst: ReactNode;
  /** ISO date the document took effect; also what the browser tab reports. */
  effective: string;
  children: ReactNode;
}

const EFFECTIVE_FORMAT = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

export function LegalLayout({ label, title, standfirst, effective, children }: LegalLayoutProps) {
  useEffect(() => {
    const previous = document.title;
    document.title = `${title} · Legate`;
    return () => {
      document.title = previous;
    };
  }, [title]);

  return (
    <div className="min-h-screen bg-paper">
      <header className="w-full border-b border-hairline">
        <div className="mx-auto w-full max-w-3xl px-5 sm:px-8 md:px-12 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <Logo className="w-6 h-6" />
            <span className="font-semibold text-base tracking-tight text-ink">Legate</span>
          </Link>
          <Link
            to="/"
            className="text-sm text-ink/70 hover:text-ink transition-colors duration-200"
          >
            Back to the site
          </Link>
        </div>
      </header>

      <main className="w-full px-4 sm:px-6 py-8 sm:py-12">
        <article className="mx-auto w-full max-w-3xl bg-paper border border-hairline shadow-sm px-5 sm:px-8 md:px-12 py-12 sm:py-16">
          <div className="text-[11px] uppercase tracking-[0.2em] text-ink/50 font-medium">
            {label}
          </div>
          <h1 className="mt-3 font-serif text-3xl sm:text-4xl font-normal leading-tight tracking-tight text-ink">
            {title}
          </h1>
          <p className="mt-5 text-sm md:text-[15px] text-ink/70 leading-relaxed max-w-2xl">
            {standfirst}
          </p>
          <div className="mt-5 text-[11px] uppercase tracking-[0.2em] text-ink/40 font-medium tabular">
            In force from {EFFECTIVE_FORMAT.format(new Date(effective))}
          </div>

          <div className="mt-10 h-px w-full bg-hairline" />

          <div className="mt-10">{children}</div>
        </article>
      </main>

      <Footer />
    </div>
  );
}

export default LegalLayout;
